import { useEffect, useRef } from "react";
import { IWebChatProps } from "../../types/WebChat/IWebChatProps";
import { getChatToken } from "../../services/apis";
import useForgeRock from "../useForgeRock";
import useGetUser from "hooks/useGetUser";

const useWebChat = ({
  widgetId,
  snippetId,
  iconType,
  openChatColor,
  openChatBackgroundColor,
  closeChatColor,
  closeChatBackgroundColor,
}: IWebChatProps) => {
  const scriptLoadedRef = useRef(false);
  const { APICall } = useForgeRock();
  const { user } = useGetUser();

  useEffect(() => {
    if (!user.firstName  || !user.isAuthenticated) return;
    if (scriptLoadedRef.current) return;

    const existingScript = document.getElementById(widgetId);
    if (existingScript) return;

    if (!window.amazon_connect) {
      window.amazon_connect = function () {
        window.amazon_connect.ac = window.amazon_connect.ac || [];
        window.amazon_connect.ac.push(arguments);
      };
    }

    const script = document.createElement("script");
    script.src = `https://${process.env.REACT_APP_WEBCHAT_API_HOST}/connectwidget/static/amazon-connect-chat-interface-client.js`;
    script.async = true;
    script.id = widgetId;

    initializeChat();

    document.head.appendChild(script);
    scriptLoadedRef.current = true;
  }, [user]);

  const initializeChat = () => {
    window.amazon_connect("snippetId", snippetId);

    // Add authentication if needed
    window.amazon_connect(
      "authenticate",
      async function (callback: (token?: string) => void) {
        const res = await APICall({
          ...getChatToken(),
        });

        callback(res?.response?.accessToken);
      }
    );

    window.amazon_connect("styles", {
      iconType,
      openChat: {
        color: openChatColor,
        backgroundColor: openChatBackgroundColor,
      },
      closeChat: {
        color: closeChatColor,
        backgroundColor: closeChatBackgroundColor,
      },
    });

    // Set customer display name
    window.amazon_connect(
      "customerDisplayName",
      function (callback: (name: string) => void) {
        callback(user.firstName);
      }
    );

    window.amazon_connect("supportedMessagingContentTypes", [
      "text/plain",
      "text/markdown",
      "application/vnd.amazonaws.connect.message.interactive",
      "application/vnd.amazonaws.connect.message.interactive.response",
    ]);
  };
};

export default useWebChat;
